import WeatherCardGrid from 'features/Weather/components/WeatherCardGrid/WeatherCardGrid';
import { useId } from 'react';
import { FiCalendar, FiMapPin } from 'react-icons/fi';
import { formatDateTime } from 'utils/dates/date.utils';
import '../../assets/fireDangerCard.scss';
import { fireDangerCardClassNames, fireDangerDetails } from '../../config/fireDanger.config';
import { getFireDangerReportDate, getFireDangerTimeOfDay } from '../../utils/fireDanger.utils';
import FireDangerIcon from '../FireDangerIcon/FireDangerIcon';

/**
 * Keeps one location's forecast together under a clear location heading.
 * Detailed forecasts get a separate row for each day; extended forecasts use dated cards.
 */
const FireDangerLocationForecast = ({ location, isDetailed }) => {
  const headingId = useId();
  const datedDayCount = location.days.filter((day) => day.date).length;

  // Both forecast types share the same cards, but use different headings and timestamps.
  const renderForecastCards = (reports) => (
    <WeatherCardGrid
      entries={reports}
      details={fireDangerDetails}
      containerClassName="fire-danger-card-grid"
      cardContainerClassName="fire-danger-time-card"
      getCardClassName={(report) => fireDangerCardClassNames[report.danger_level]}
      cardHeadingLevel={isDetailed ? 'h6' : 'h5'}
      getCardKey={(report, index) => `${report.utc_time ?? 'date'}-${index}`}
      getCardTitle={(report) =>
        isDetailed
          ? getFireDangerTimeOfDay(report)
          : formatDateTime(getFireDangerReportDate(report), 'short', 'Date unavailable', true)
      }
      getCardSubtitle={
        isDetailed
          ? (report) => {
              const date = getFireDangerReportDate(report);
              if (!date) return 'Time unavailable';

              // The date is already above the row. Show the exact time and zone on each card,
              // while keeping the full timestamp available to screen readers.
              return (
                <time dateTime={date.toISOString()} aria-label={formatDateTime(date)}>
                  {date.toLocaleTimeString(undefined, {
                    hour: 'numeric',
                    minute: '2-digit',
                    timeZoneName: 'short',
                  })}
                </time>
              );
            }
          : undefined
      }
      getCardIcon={(report) => <FireDangerIcon dangerLevel={report.danger_level} />}
    />
  );

  return (
    <section
      aria-labelledby={headingId}
      className="fire-danger-location rounded-3 border p-3 p-xl-4 d-flex flex-column gap-4"
    >
      <div className="fire-danger-location-header d-flex align-items-center justify-content-between flex-wrap gap-2 border-bottom pb-3">
        <div className="d-flex align-items-center gap-2 min-w-0">
          <FiMapPin className="flex-shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <span className="fire-danger-caption d-block small">Location</span>
            <h4 id={headingId} className="fire-danger-location-title m-0 fw-bold">
              {location.name}
            </h4>
          </div>
        </div>
        <span className="fire-danger-caption small">
          {datedDayCount
            ? `${datedDayCount} ${datedDayCount === 1 ? 'date' : 'dates'}`
            : 'Date unavailable'}
        </span>
      </div>

      {isDetailed
        ? location.days.map((day) => {
            const dayHeadingId = `${headingId}-${day.key}`;
            return (
              <section
                key={day.key}
                aria-labelledby={dayHeadingId}
                className="fire-danger-day d-flex flex-column gap-3"
              >
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <h5 id={dayHeadingId} className="fire-danger-date-title m-0 fw-semibold">
                    <FiCalendar className="me-2" aria-hidden="true" />
                    {formatDateTime(day.date, 'long', 'Date unavailable', true)}
                  </h5>
                  <span className="fire-danger-caption small">
                    {day.reports.length} {day.reports.length === 1 ? 'report' : 'reports'}
                  </span>
                </div>
                {renderForecastCards(day.reports)}
              </section>
            );
          })
        : renderForecastCards(location.days.flatMap((day) => day.reports))}
    </section>
  );
};

export default FireDangerLocationForecast;
